import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, getStaticUrl } from '../../api/client';
import './CalendarView.css';

// --- API helpers ---

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const defaultHeaders = { 'Content-Type': 'application/json' };
  if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, {
    ...options,
    headers: { ...defaultHeaders, ...(options.headers || {}) },
  });

  const contentType = res.headers.get('content-type');

  if (!res.ok) {
    if (contentType && contentType.includes('application/json')) {
      const errorData = await res.json().catch(() => ({ detail: "Erreur de parsing" }));
      throw new Error(`Erreur API ${res.status} (URL: ${url}): ${errorData.detail}`);
    } else {
      const text = await res.text();
      console.error(`Réponse non-JSON (${res.status}) pour ${url}. Contenu: ${text.substring(0, 200)}`);
      throw new Error(`Erreur API ${res.status}: Le serveur n'a pas renvoyé de JSON. (URL: ${url})`);
    }
  }

  try {
    return res.json();
  } catch (e) {
    const d = e.message.includes('<') ? 'Contenu HTML inattendu.' : e.message;
    throw new Error(`Erreur JSON: ${url} — ${d}`);
  }
};

const teamsApi = {
  getAll: async () => { const data = await apiFetch(`${API_BASE_URL}/teams/`); return { data }; },
  getById: async (id) => { const data = await apiFetch(`${API_BASE_URL}/teams/${id}`); return { data }; },
};

const usersApi = {
  getAll: async ({ team_id }) => {
    const url = team_id ? `${API_BASE_URL}/auth/users?team_id=${team_id}` : `${API_BASE_URL}/auth/users`;
    const data = await apiFetch(url);
    return { data };
  },
};

const eventsApi = {
  getAll: async ({ team_id }) => { const data = await apiFetch(`${API_BASE_URL}/events/?team_id=${team_id}`); return { data }; },
  create: async (eventData) => { const data = await apiFetch(`${API_BASE_URL}/events/`, { method: 'POST', body: JSON.stringify(eventData) }); return { data }; },
};

const availabilityApi = {
  getTeamAvailability: async (teamId, { start_date, end_date }) => {
    const data = await apiFetch(`${API_BASE_URL}/availability/team/${teamId}?start_date=${start_date}&end_date=${end_date}`);
    return { data };
  },
  syncPlayerDay: async (playerId, date, ranges) => {
    return apiFetch(`${API_BASE_URL}/availability/sync`, {
      method: 'POST',
      body: JSON.stringify({ user_id: playerId, date, ranges }),
    });
  },
};

// --- Component ---

const getInitials = (username) => username?.charAt(0).toUpperCase() || '?';

const getRoleName = (role) => ({
  ceo: 'CEO', coo: 'COO', responsable: 'Responsable',
  manager: 'Manager', coach: 'Coach', player: 'Joueur',
}[role] || role);

const getRoleDotColor = (role) => ({
  ceo: 'var(--tertiary)',
  coo: 'var(--tertiary)',
  responsable: 'var(--primary)',
  manager: 'var(--primary)',
  coach: 'var(--secondary)',
  player: '#468dff',
}[role] || 'var(--text-muted)');

const CalendarView = () => {
  const { user, hasPermission, loading } = useAuth();
  const location = useLocation();

  const canEditTeamAvailability = hasPermission('availability.edit_team') || hasPermission('availability.edit_all');
  const canEditAvailability = hasPermission('availability.edit_own') || canEditTeamAvailability;
  const canCreateEvent = hasPermission('events.create');

  const [teamId, setTeamId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [defectiveUrl, setDefectiveUrl] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const teamParam = params.get('team');
    setTeamId(teamParam ? parseInt(teamParam, 10) : null);
  }, [location.search]);

  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [events, setEvents] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);

  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);

  const [showEventForm, setShowEventForm] = useState(false);
  const [eventFormData, setEventFormData] = useState({
    title: '', type: 'training', start_datetime: '', end_datetime: '', notes: '',
  });

  useEffect(() => {
    if (loading) return;
    if (!user) { setIsLoading(false); setErrorMessage('Vous devez être connecté.'); return; }
    const params = new URLSearchParams(location.search);
    if (!params.get('team') && !teamId) loadDefaultTeam();
  }, [user, loading]);

  useEffect(() => {
    if (loading || !user || !teamId) return;
    loadTeamData();
  }, [teamId, currentDate, viewMode, user, loading]);

  const loadDefaultTeam = async () => {
    setIsLoading(true);
    try {
      const res = await teamsApi.getAll();
      if (res.data?.length > 0) setTeamId(res.data[0].id);
      else { setIsLoading(false); setErrorMessage("Aucune équipe disponible."); }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setErrorMessage("Erreur lors du chargement des équipes");
    }
  };

  const loadTeamData = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setDefectiveUrl(null);
    try {
      const { startDate, endDate } = getDateRange();
      const [teamRes, usersRes, eventsRes, availRes] = await Promise.all([
        teamsApi.getById(teamId),
        usersApi.getAll({ team_id: teamId }),
        eventsApi.getAll({ team_id: teamId }),
        availabilityApi.getTeamAvailability(teamId, {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
        }),
      ]);
      setTeam(teamRes.data);
      setPlayers(usersRes.data);
      if (canEditTeamAvailability) {
        setSelectedPlayer(prev => prev || usersRes.data[0]?.id || null);
      } else {
        const me = usersRes.data.find(u => u.id === user?.id);
        setSelectedPlayer(canEditAvailability ? (me?.id || null) : null);
      }
      setEvents(eventsRes.data);
      setAvailabilities(availRes.data);
    } catch (error) {
      console.error(error);
      const urlMatch = error.message.match(/(URL: (.*?)\)|L'URL (.*?) —)/);
      let url = 'Inconnue';
      if (urlMatch) { url = (urlMatch[2] || urlMatch[3] || '').replace(/\.$/, '').trim(); }
      setErrorMessage(error.message);
      setDefectiveUrl(url);
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRange = () => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    if (viewMode === 'week') {
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      end.setDate(start.getDate() + 6);
    } else {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1, 0);
    }
    return { startDate: start, endDate: end };
  };

  const getDaysInRange = () => {
    const { startDate, endDate } = getDateRange();
    const days = [];
    const current = new Date(startDate);
    while (current <= endDate) { days.push(new Date(current)); current.setDate(current.getDate() + 1); }
    return days;
  };

  const hours = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2];

  const handleCellMouseDown = (day, hour) => {
    if (!selectedPlayer || !canEditAvailability) return;
    if (selectedPlayer !== user?.id && !canEditTeamAvailability) return;
    setIsSelecting(true);
    setSelectionStart({ day, hour });
  };

  const handleCellMouseUp = async (day, hour) => {
    if (!isSelecting || !selectionStart || !selectedPlayer) return;
    if (day.toISOString().split('T')[0] !== selectionStart.day.toISOString().split('T')[0]) {
      setIsSelecting(false); setSelectionStart(null); return;
    }
    setIsSelecting(false);
    const startHour = Math.min(selectionStart.hour, hour);
    const endHour = Math.min(Math.max(selectionStart.hour, hour) + 1, 24);
    const dateStr = day.toISOString().split('T')[0];
    const currentSelectedPlayer = selectedPlayer;
    try {
      const currentDayAvail = availabilities
        .filter(a => a.user_id === selectedPlayer && a.date === dateStr)
        .map(a => {
          const start = parseInt(a.start_time.split(':')[0]);
          let end = parseInt(a.end_time.split(':')[0]);
          if (end === 0) end = 24;
          return { start, end };
        });
      const newRange = { start: startHour, end: endHour };
      const isInsideExisting = currentDayAvail.some(a => newRange.start >= a.start && newRange.end <= a.end);
      let updatedAvail;
      if (isInsideExisting) {
        updatedAvail = [];
        for (const a of currentDayAvail) {
          if (newRange.end <= a.start || newRange.start >= a.end) { updatedAvail.push(a); }
          else {
            if (newRange.start > a.start) updatedAvail.push({ start: a.start, end: newRange.start });
            if (newRange.end < a.end) updatedAvail.push({ start: newRange.end, end: a.end });
          }
        }
      } else {
        updatedAvail = [...currentDayAvail, newRange];
        updatedAvail.sort((a, b) => a.start - b.start);
        updatedAvail = updatedAvail.reduce((merged, current) => {
          if (!merged.length) return [current];
          const last = merged[merged.length - 1];
          if (current.start <= last.end) { last.end = Math.max(last.end, current.end); } else { merged.push(current); }
          return merged;
        }, []);
      }
      await availabilityApi.syncPlayerDay(selectedPlayer, dateStr, updatedAvail);
      await loadTeamData();
      setSelectedPlayer(currentSelectedPlayer);
    } catch (error) {
      console.error('Erreur sauvegarde disponibilité:', error);
    }
    setSelectionStart(null);
  };

  const isPlayerAvailable = (playerId, day, hour) => {
    const dateStr = day.toISOString().split('T')[0];
    return availabilities.some(avail => {
      if (avail.user_id !== playerId || avail.date !== dateStr) return false;
      const startHour = parseInt(avail.start_time.split(':')[0]);
      let endHour = parseInt(avail.end_time.split(':')[0]);
      if (endHour === 0) endHour = 24;
      return hour >= startHour && hour < endHour;
    });
  };

  const getTeamAvailabilityCount = (day, hour) => {
    if (players.length === 0) return 0;
    return players.filter(p => isPlayerAvailable(p.id, day, hour)).length;
  };

  const isAllTeamAvailable = (day, hour) => {
    if (players.length === 0) return false;
    return getTeamAvailabilityCount(day, hour) === players.length;
  };

  const getGlobalStatus = () => {
    const days = getDaysInRange();
    let anyAll = false, anyPartial = false;
    for (const day of days) {
      for (const hour of hours) {
        const count = getTeamAvailabilityCount(day, hour);
        if (count === players.length && players.length > 0) anyAll = true;
        else if (count > 0) anyPartial = true;
      }
    }
    if (anyAll) return 'all';
    if (anyPartial) return 'partial';
    return 'none';
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    if (!teamId) return;
    try {
      await eventsApi.create({ ...eventFormData, team_id: parseInt(teamId) });
      setEventFormData({ title: '', type: 'training', start_datetime: '', end_datetime: '', notes: '' });
      setShowEventForm(false);
      loadTeamData();
    } catch (error) {
      console.error('Erreur création événement:', error.message);
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') newDate.setDate(newDate.getDate() + direction * 7);
    else newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const formatDateHeader = () => {
    if (viewMode === 'week') {
      const { startDate, endDate } = getDateRange();
      return `${startDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} – ${endDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  // --- Empty/loading/error states ---

  if (!teamId) {
    return (
      <div className="cv-page">
        <p className="cv-empty">Aucune équipe sélectionnée — ajoutez <code>?team=1</code> à l'URL.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="cv-page">
        <p className="cv-empty">Chargement des données…</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="cv-page">
        <div className="cv-error">
          <h2>Erreur de chargement</h2>
          <p>L'application n'a pas pu charger les données.</p>
          <p><strong>URL :</strong> <code>{defectiveUrl || 'inconnue'}</code></p>
          <p><code style={{ fontSize: '11px' }}>{errorMessage}</code></p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="cv-page">
        <p className="cv-empty">Équipe introuvable (ID {teamId}).</p>
      </div>
    );
  }

  const days = getDaysInRange();
  const globalStatus = getGlobalStatus();

  const statusLabel = {
    all: 'Équipe disponible',
    partial: 'Partiellement disponible',
    none: 'Aucune disponibilité',
  }[globalStatus];

  return (
    <div className="cv-page">
      {/* Header */}
      <header className="cv-header">
        <div>
          <h1 className="cv-title">Planning — {team.name}</h1>
          <p className="cv-subtitle">{players.length} joueur{players.length > 1 ? 's' : ''}</p>
        </div>
        <div className="cv-header-actions">
          <div className={`cv-status-pill${globalStatus === 'partial' ? ' cv-status-pill--partial' : globalStatus === 'none' ? ' cv-status-pill--none' : ''}`}>
            <span className="cv-status-dot" />
            {statusLabel}
          </div>
          {canCreateEvent && (
            <button className="cv-btn-event" onClick={() => setShowEventForm(!showEventForm)}>
              {showEventForm ? 'Fermer' : '+ Événement'}
            </button>
          )}
        </div>
      </header>

      {/* Event form */}
      {showEventForm && (
        <form onSubmit={handleEventSubmit} className="cv-event-form">
          <input
            type="text"
            placeholder="Titre de l'événement"
            value={eventFormData.title}
            onChange={e => setEventFormData({ ...eventFormData, title: e.target.value })}
            required
          />
          <select
            value={eventFormData.type}
            onChange={e => setEventFormData({ ...eventFormData, type: e.target.value })}
          >
            <option value="training">Entraînement</option>
            <option value="scrim">Scrim</option>
            <option value="tournament">Tournoi</option>
            <option value="coaching">Coaching</option>
            <option value="meeting">Réunion</option>
          </select>
          <input
            type="datetime-local"
            value={eventFormData.start_datetime}
            onChange={e => setEventFormData({ ...eventFormData, start_datetime: e.target.value })}
            required
          />
          <input
            type="datetime-local"
            value={eventFormData.end_datetime}
            onChange={e => setEventFormData({ ...eventFormData, end_datetime: e.target.value })}
            required
          />
          <button type="submit" className="cv-btn-create">Créer</button>
        </form>
      )}

      {/* Body: roster + grid */}
      <div className="cv-body">
        {/* Roster panel */}
        <aside className="cv-roster">
          <p className="cv-roster-title">Joueurs</p>
          {players.map(player => (
            <div
              key={player.id}
              className={`cv-roster-player${selectedPlayer === player.id ? ' cv-roster-player--active' : ''}`}
              onClick={() => canEditTeamAvailability && setSelectedPlayer(player.id)}
              style={{ cursor: canEditTeamAvailability ? 'pointer' : 'default' }}
            >
              <div className="cv-roster-avatar">
                {player.avatar
                  ? <img src={getStaticUrl(player.avatar)} alt={player.username} />
                  : <span>{getInitials(player.username)}</span>
                }
              </div>
              <div className="cv-roster-info">
                <div className="cv-roster-name">{player.username}</div>
                <div className="cv-roster-role">{getRoleName(player.role)}</div>
              </div>
              <span
                className="cv-roster-dot"
                style={{ background: getRoleDotColor(player.role) }}
              />
            </div>
          ))}
        </aside>

        {/* Main panel */}
        <div className="cv-main">
          {/* Controls */}
          <div className="cv-controls">
            <div className="cv-view-toggle">
              <button
                className={`cv-view-btn${viewMode === 'week' ? ' cv-view-btn--active' : ''}`}
                onClick={() => setViewMode('week')}
              >
                Semaine
              </button>
              <button
                className={`cv-view-btn${viewMode === 'month' ? ' cv-view-btn--active' : ''}`}
                onClick={() => setViewMode('month')}
              >
                Mois
              </button>
            </div>

            <div className="cv-date-nav">
              <button className="cv-nav-btn" onClick={() => navigateDate(-1)}>←</button>
              <span className="cv-current-period">{formatDateHeader()}</span>
              <button className="cv-nav-btn" onClick={() => navigateDate(1)}>→</button>
            </div>
          </div>

          {/* Legend */}
          <div className="cv-legend">
            <div className="cv-legend-item">
              <span className="cv-legend-dot cv-legend-dot--all" />
              <span className="cv-legend-label">Toute l'équipe</span>
            </div>
            <div className="cv-legend-item">
              <span className="cv-legend-dot cv-legend-dot--player" />
              <span className="cv-legend-label">Joueur sélectionné</span>
            </div>
            <div className="cv-legend-item">
              <span className="cv-legend-dot cv-legend-dot--partial" />
              <span className="cv-legend-label">Partiellement disponible</span>
            </div>
          </div>

          {/* Grid */}
          <div className="cv-grid-wrapper">
            <div className="cv-grid-scroll">
              <div className="cv-grid">
                {/* Time column */}
                <div className="cv-time-col">
                  <div className="cv-time-header">H</div>
                  {hours.map(hour => (
                    <div key={hour} className="cv-time-cell">
                      {String(hour).padStart(2, '0')}h
                    </div>
                  ))}
                </div>

                {/* Day columns */}
                {days.map((day, dayIndex) => (
                  <div key={dayIndex} className="cv-day-col">
                    <div className="cv-day-header">
                      <div className="cv-day-name">
                        {day.toLocaleDateString('fr-FR', { weekday: 'short' })}
                      </div>
                      <div className="cv-day-number">{day.getDate()}</div>
                    </div>
                    {hours.map(hour => {
                      const teamCount = getTeamAvailabilityCount(day, hour);
                      const allAvailable = isAllTeamAvailable(day, hour);
                      const playerAvailable = selectedPlayer ? isPlayerAvailable(selectedPlayer, day, hour) : false;

                      let cellClass = 'cv-cell';
                      if (allAvailable) cellClass += ' cv-cell--all';
                      else if (playerAvailable) cellClass += ' cv-cell--player';
                      else if (teamCount > 0) cellClass += ' cv-cell--partial';

                      return (
                        <div
                          key={hour}
                          className={cellClass}
                          onMouseDown={() => handleCellMouseDown(day, hour)}
                          onMouseUp={() => handleCellMouseUp(day, hour)}
                          title={`${teamCount}/${players.length} disponible(s)`}
                        >
                          {teamCount > 0 && !allAvailable && !playerAvailable && (
                            <span className="cv-cell-count">{teamCount}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="cv-tip">
            <strong>Astuce :</strong>&nbsp;
            Cliquez et glissez sur les cellules pour définir vos disponibilités
            {canEditTeamAvailability ? ' pour le joueur sélectionné' : ''}.
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
